import React, { Component } from 'react';
import axios from 'axios';
import { PrismLoading } from '@prism-ui/react';
import { StepIndicator } from '../StepIndicator';
import InfoStep from '../InfoStep';
import ChapterStep from '../ChapterStep';
import Requirements from '../Requirements';
import ApplicantProfileForm from '../ApplicantProfileForm';
import RelationshipStep from '../RelationshipStep';
import CompetenciesStep from '../CompetenciesStep';
import EssayStep from '../EssayStep';
import './ApplicationContainer.css';
import { ApplicationWindowContextProvider } from './ApplicationWindowContext';
import {
  APPLICATION_STATUS_ID_SUBMITTED,
  APPLICATION_STATUS_ID_WITHDRAWN,
  APPLICATION_STATUS_ID_DRAFT,
  APP_TYPE_ID_TO_STRING,
  APPLICANT_TYPE_MENTEE,
} from '../constants';
import ReviewApplicationStep from '../ReviewApplicationStep/index.jsx';
import { ResumePrompt } from '../ResumePrompt';
import { prefetchChapterStepData } from '../ChapterStep/chapterStepData';
import warmPrismComponents from '../../../utils/warmPrismComponents';
import waitForPrismHydration from '../../../utils/waitForPrismHydration';

// Prism chunks load on first connect, so anything first used on a later step would otherwise
// pop in after the step has painted (and an un-hydrated prism-popover lays out in-flow).
const APPLY_FLOW_PRISM_TAGS = [
  'prism-select',
  'prism-card',
  'prism-badge',
  'prism-banner',
  'prism-checkbox',
  'prism-popover',
];

class ApplicationContainer extends Component {

  _isMounted = false;

  state = {
    loading: true,
    application: null,
    educationEntries: [],
    careerEntries: [],
    competencies: [],
    competenciesRatings: [],
    applicantTypeId: null,
    managerSubmissions: [],
    step: 0,
    selectedChapterId: null,
    showResumePrompt: false,
    draftLastStep: null,
    transitioning: false,
  };

  stepContentRef = React.createRef();

  jumpToStep = (step) => {
    const token = (this._stepToken = (this._stepToken || 0) + 1);
    this.setState({ transitioning: true });
    setTimeout(() => {
      if (token !== this._stepToken || !this._isMounted) return;
      // Start each step at the top; done while faded so the jump is never seen.
      if (typeof window !== 'undefined' && window.scrollY > 0) {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
      // Swap the step while still faded, and only fade back in once its Prism controls have
      // hydrated; otherwise the heading and buttons paint first and the cards pop in after.
      this.setState({ step }, () => {
        waitForPrismHydration(this.stepContentRef.current).then(() => {
          if (token === this._stepToken && this._isMounted) {
            this.setState({ transitioning: false });
          }
        });
      });
      this.saveDraftStep(step);
    }, 150);
  };

  setSelectedChapter = (chapterId) => {
    this.setState({ selectedChapterId: chapterId });
  };

  setApplicantType = (id) => {
    this.setState({ applicantTypeId: id });
  };

  refreshApplicationState = async () => {
    try {
      const response = await axios.get('/api/applications/my_application');
      const submissions = response.data.filter(
        (s) => s.year === new Date().getFullYear()
      );
      let application = submissions.find((x) => {
        const isActive = x.active === true;
        const isSubmitted = x.status_id === APPLICATION_STATUS_ID_SUBMITTED;
        const currentYear = x.year === new Date().getFullYear();
        const isUser = x.user_id === this.props.currentUser.user_id;
        return (isActive || isSubmitted) && currentYear && isUser;
      }) || submissions.find((x) => {
        const isWithdrawn = x.status_id === APPLICATION_STATUS_ID_WITHDRAWN;
        const currentYear = x.year === new Date().getFullYear();
        const isUser = x.user_id === this.props.currentUser.user_id;
        return isWithdrawn && currentYear && isUser;
      });

      // After a withdrawal, keep the app reference for data but clear applicantTypeId
      // so the user lands on role selection
      const isWithdrawnApp = application?.status_id === APPLICATION_STATUS_ID_WITHDRAWN;

      this.setState({
        application: application || null,
        applicantTypeId: isWithdrawnApp ? null : (application?.applicant_type_id || null),
        competencies: [],
        competenciesRatings: [],
        educationEntries: [],
        careerEntries: [],
      });

      if (application) {
        // Pass the id explicitly: React 18 batches the setState above, so
        // this.state.application is not updated yet at this point.
        this.loadEduction(application.id);
        this.loadCareerEntries(application.id);
        const ratingsResponse = await axios.get(
          `/api/competencies_ratings?application_id=${application.id}`
        );
        this.setState({ competenciesRatings: ratingsResponse.data });
      }
    } catch (error) {
      console.error('Error refreshing application state:', error);
    }
  };

  updateApplication = (application) => {
    return axios
      .put(`/api/applications/${application.id}`, application)
      .then((response) => {
        this.setState(
          {
            application: application,
          },
          () => {
            return Promise.resolve();
          }
        );
      });
  };

  updateApplicationState = (updates) => {
    this.setState({
      application: {
        ...this.state.application,
        ...updates
      }
    });
  };

  saveApplication = async (applicationData) => {
    try {
      let response;

      if (this.state.application && this.state.application.id) {
        // Auto-reactivate withdrawn apps when saving new data
        if (this.state.application.status_id === APPLICATION_STATUS_ID_WITHDRAWN) {
          applicationData = { ...applicationData, status_id: APPLICATION_STATUS_ID_DRAFT, active: true, withdrawn_at: null };
        }
        // Update existing application - make sure chapter_id is included
        response = await axios.put(
          `/api/applications/${this.state.application.id}`,
          applicationData
        );
      } else {
        // Create new application - include chapter_id and required fields
        try {
          response = await axios.post('/api/applications', {
            user_id: this.props.currentUser.id,
            ...applicationData, // This will include chapter_id from ChapterStep
          });
        } catch (createError) {
          // The server allows one application per person per year; carry on with the one that exists.
          const existing = createError.response?.status === 409 && createError.response.data?.application;
          if (!existing) throw createError;
          response = { data: existing };
        }
      }

      this.setState({
        application: response.data,
      });

      return response.data;
    } catch (error) {
      console.error('Error saving application:', error);
      // Log the specific error details
      if (error.response?.data) {
        console.error('API Error Details:', error.response.data);
      }
      throw error; // Re-throw if you want ChapterStep to handle the error
    }
  };

  createEducation = (education) => {
    if (!this._isMounted) return;

    // Add null check for application
    if (!this.state.application || !this.state.application.id) {
      console.error(
        'Cannot create education: Application not found or missing ID'
      );
      console.log('Current application state:', this.state.application);
      return;
    }

    if (
      education?.application_id === null ||
      education?.application_id === undefined
    ) {
      education.application_id = this.state.application.id;
    }

    axios
      .post('/api/education_entries', education)
      .then((response) => {
        if (this._isMounted) {
          this.setState(
            {
              educationEntries: [...this.state.educationEntries, response.data],
            },
            () => {
              return Promise.resolve();
            }
          );
        }
      })
      .catch((error) => {
        console.error('Error creating education:', error);
      });
  };

  handleDeleteEducation = (id) => {
    const existingEducation = this.state.educationEntries;
    const recordToBeDeleted = existingEducation[id];
    existingEducation.splice(id, 1);

    // delete the row from the db if the row already exists, otherwise just remove it from the client
    if (recordToBeDeleted && Object.hasOwn(recordToBeDeleted, 'id')) {
      axios
        .delete(`/api/education_entries/${recordToBeDeleted.id}`)
        .then((result) => {
          this.setState({ educationEntries: existingEducation });
        });
    } else {
      this.setState({ educationEntries: existingEducation });
    }
  };

  updateEducation = (index, updatedData) => {
    const entry = this.state.educationEntries[index];
    if (!entry || !entry.id) return;
    axios
      .put(`/api/education_entries/${entry.id}`, updatedData)
      .then((response) => {
        if (this._isMounted) {
          const updated = [...this.state.educationEntries];
          updated[index] = response.data;
          this.setState({ educationEntries: updated });
        }
      })
      .catch((error) => {
        console.error('Error updating education:', error);
      });
  };

  createCareer = (career) => {
    if (!this._isMounted) return Promise.resolve();

    // Add null check for application
    if (!this.state.application || !this.state.application.id) {
      console.error(
        'Cannot create career: Application not found or missing ID'
      );
      return Promise.resolve();
    }

    const payload = { ...career };

    if (payload.end_date?.trim?.().length === 0) {
      payload.end_date = null;
    }

    if (payload.application_id == null) {
      payload.application_id = this.state.application.id;
    }

    return axios
      .post('/api/career_entries', payload)
      .then((response) => {
        if (this._isMounted) {
          this.setState({
            careerEntries: [...this.state.careerEntries, response.data],
          });
        }
      })
      .catch((error) => {
        console.error('Error creating career:', error);
      });
  };

  handleDeleteCareer = (id) => {
    const recordToBeDeleted = this.state.careerEntries[id];
    const remaining = this.state.careerEntries.filter((_, i) => i !== id);
    if (recordToBeDeleted && Object.hasOwn(recordToBeDeleted, 'id')) {
      axios
        .delete(`/api/career_entries/${recordToBeDeleted.id}`)
        .then(() => {
          this.setState({ careerEntries: remaining });
        });
    } else {
      this.setState({ careerEntries: remaining });
    }
  };

  updateCareer = (index, updatedData) => {
    const entry = this.state.careerEntries[index];
    if (!entry || !entry.id) return;
    if (updatedData?.end_date?.trim?.()?.length === 0) {
      updatedData.end_date = null;
    }
    axios
      .put(`/api/career_entries/${entry.id}`, updatedData)
      .then((response) => {
        if (this._isMounted) {
          const updated = [...this.state.careerEntries];
          updated[index] = response.data;
          this.setState({ careerEntries: updated });
        }
      })
      .catch((error) => {
        console.error('Error updating career:', error);
      });
  };

  getCompetencies = (applicant_type) => {
    return axios
      .get('/api/competencies?applicant_type_id=' + applicant_type)
      .then((response) => {
        this.setState({ competencies: response.data });
        return Promise.resolve();
      });
  };

  createCompetenciesRatings = (ratings) => {
    return axios
      .post(
        '/api/competencies_ratings?application_id=' + this.state.application.id,
        ratings
      )
      .then((response) => {
        this.setState(
          {
            competenciesRatings: response.data,
          },
          () => {
            return Promise.resolve();
          }
        );
      });
  };

  // Copy competency ratings from the current track to a new track, matched by name
  copyRatingsToNewTrack = async (oldTypeId, newTypeId) => {
    const applicationId = this.state.application?.id;
    const currentRatings = this.state.competenciesRatings;
    if (!applicationId || !currentRatings?.length) return;

    try {
      // Fetch competencies for both tracks
      const [oldCompRes, newCompRes] = await Promise.all([
        axios.get('/api/competencies?applicant_type_id=' + oldTypeId),
        axios.get('/api/competencies?applicant_type_id=' + newTypeId),
      ]);

      // Build name→id map for the new track
      const newCompByName = {};
      newCompRes.data.forEach((c) => { newCompByName[c.name] = c.id; });

      // Build old competency id→name map
      const oldNameById = {};
      oldCompRes.data.forEach((c) => { oldNameById[c.id] = c.name; });

      // Map old ratings to new competency IDs by name
      const newRatings = currentRatings
        .map((r) => {
          const name = oldNameById[r.competency];
          const newCompId = name ? newCompByName[name] : null;
          if (!newCompId) return null;
          return {
            competency: newCompId,
            rating: r.rating,
            examples: r.examples,
            application_id: applicationId,
          };
        })
        .filter(Boolean);

      if (newRatings.length > 0) {
        await this.createCompetenciesRatings(newRatings);
      }
    } catch (error) {
      console.error('Error copying ratings to new track:', error);
    }
  };

  loadEduction = (applicationId = this.state.application.id) => {
    axios
      .get(`/api/education_entries?application_id=${applicationId}`)
      .then((response) => {
        this.setState({ educationEntries: response.data });
      });
  };

  loadCareerEntries = (applicationId = this.state.application.id) => {
    axios
      .get(`/api/career_entries?application_id=${applicationId}`)
      .then((response) => {
        this.setState({ careerEntries: response.data });
      });
  };

  getDraftKey = () => {
    const userId = this.props.currentUser?.user_id || this.props.currentUser?.User?.id;
    return `clear_draft_${userId}_${new Date().getFullYear()}`;
  };

  saveDraftToLocalStorage = (stepIndex) => {
    try {
      const key = this.getDraftKey();
      const existing = JSON.parse(localStorage.getItem(key) || '{}');
      existing.lastStepIndex = stepIndex;
      existing.updatedAt = new Date().toISOString();
      localStorage.setItem(key, JSON.stringify(existing));
    } catch {
      // localStorage may be unavailable
    }
  };

  clearDraftFromLocalStorage = () => {
    try {
      localStorage.removeItem(this.getDraftKey());
    } catch {
      // localStorage may be unavailable
    }
  };

  saveDraftStep = async (step) => {
    if (!this.state.application?.id || this.state.application.status_id === APPLICATION_STATUS_ID_SUBMITTED) return;
    this.saveDraftToLocalStorage(step);
    try {
      await axios.put(`/api/applications/${this.state.application.id}/draft`, {
        last_step_index: step,
      });
    } catch (err) {
      console.warn('Failed to save draft step index:', err);
    }
  };

  handleResumeContinue = (stepIndex) => {
    const step = stepIndex ?? this.state.draftLastStep ?? 0;
    this.setState({ showResumePrompt: false, step });
    this.saveDraftStep(step);
  };

  handleStartFresh = async () => {
    this.clearDraftFromLocalStorage();
    if (this.state.application?.id) {
      try {
        await axios.put(`/api/applications/${this.state.application.id}/draft`, {
          last_step_index: null,
        });
      } catch {
        // Non-critical
      }
    }
    this.setState({ showResumePrompt: false, step: 0, draftLastStep: null });
  };

  componentDidMount() {
    this._isMounted = true;
    // Load what the Chapter step needs while the applicant reads Info, so Begin goes straight
    // to a painted step instead of a spinner screen with controls popping in after it.
    prefetchChapterStepData();
    this._cancelWarmup = warmPrismComponents(APPLY_FLOW_PRISM_TAGS);
    axios
      .get('/api/applications/my_application')
      .then(async (response) => {
        const submissions = response.data.filter(
          (s) => s.year === new Date().getFullYear()
        );

        // set the application to the one that is active
        // isUser prevents managers from accidentally editing on of their reports' application
        // Prefer active/submitted apps; fall back to withdrawn
        let application = submissions.find((x) => {
          const isActive = x.active === true;
          const isSubmitted = x.status_id === APPLICATION_STATUS_ID_SUBMITTED;
          const currentYear = x.year === new Date().getFullYear();
          const isUser = x.user_id === this.props.currentUser.user_id;
          return (isActive || isSubmitted) && currentYear && isUser;
        }) || submissions.find((x) => {
          const isWithdrawn = x.status_id === APPLICATION_STATUS_ID_WITHDRAWN;
          const currentYear = x.year === new Date().getFullYear();
          const isUser = x.user_id === this.props.currentUser.user_id;
          return isWithdrawn && currentYear && isUser;
        });

        // Auto-reactivate withdrawn apps back to draft on page load
        if (application && application.status_id === APPLICATION_STATUS_ID_WITHDRAWN) {
          await axios.put(`/api/applications/${application.id}`, {
            status_id: APPLICATION_STATUS_ID_DRAFT,
            active: true,
            withdrawn_at: null,
          });
          application = { ...application, status_id: APPLICATION_STATUS_ID_DRAFT, active: true, withdrawn_at: null };
        }

        if (application) {
          this.setState({ application, applicantTypeId: application.applicant_type_id || null }, () => {
            this.loadEduction();
            this.loadCareerEntries();
          });

          axios
            .get(`/api/competencies_ratings?application_id=${application.id}`)
            .then((response) => {
              this.setState({ competenciesRatings: response.data });
            });

          // Check for draft resume
          if (application.status_id !== APPLICATION_STATUS_ID_SUBMITTED) {
            let lastStep = application.last_step_index;

            // Check localStorage for more recent data
            try {
              const key = this.getDraftKey();
              const localDraft = JSON.parse(localStorage.getItem(key) || '{}');
              if (localDraft.lastStepIndex != null) {
                // Use the more recent of server vs localStorage
                if (!lastStep || (localDraft.updatedAt && new Date(localDraft.updatedAt) > new Date(application.updated_at))) {
                  lastStep = localDraft.lastStepIndex;
                }
              }
            } catch {
              // localStorage may be unavailable
            }

            if (lastStep != null && lastStep > 0) {
              this.setState({ showResumePrompt: true, draftLastStep: lastStep });
            }
          }
        }
        const managerSubmissions = submissions.filter((x) => {
          const isSubmitted = x.status_id === APPLICATION_STATUS_ID_SUBMITTED;
          const currentYear = x.year === new Date().getFullYear();
          const isUser = x.user_id === this.props.currentUser.user_id;
          return currentYear && isSubmitted && !isUser;
        });
        this.setState({ managerSubmissions });
      })
      .finally(() => this.setState({ loading: false }));
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this._cancelWarmup) this._cancelWarmup();
    // Save current step when navigating away from the Apply page
    if (this.state.step > 0) {
      this.saveDraftStep(this.state.step);
    }
  }

  render() {
    if (this.state.loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
          <PrismLoading as="spinner" container="inline" />
        </div>
      );
    }

    const applicantTypeId =
      this.state.application?.applicant_type_id || this.state.applicantTypeId;

    const chapter_id =
      this.state.application?.chapter_id || this.props.currentUser?.chapter_id;

    console.log('Rendering ApplicationContainer with chapter_id:', chapter_id);

    const isApplicationSubmitted =
      this.state.application &&
      this.state.application.status_id === APPLICATION_STATUS_ID_SUBMITTED;
    const showStepsCondition =
      !this.state.application || !isApplicationSubmitted;
    const applicantTypeString =
      APP_TYPE_ID_TO_STRING[this.state.applicantTypeId] || '';

    const steps = [
      {
        name: 'Info',
        component: (
          <InfoStep
            currentUser={this.props.currentUser}
            application={this.state.application}
            saveApplication={this.saveApplication}
            jumpToStep={this.jumpToStep}
          />
        ),
      },
      {
        name: 'Chapter',
        component: (
          <ChapterStep
            currentUser={this.props.currentUser}
            application={this.state.application}
            updateApplicationState={this.updateApplicationState}  // State-only updates
            saveApplication={this.saveApplication}
            jumpToStep={this.jumpToStep}
            selectedChapterId={this.state.selectedChapterId}
            setSelectedChapter={this.setSelectedChapter}
          />
        ),
      },
      {
        name: 'Role',
        component: (
          <Requirements
            currentUser={this.props.currentUser}
            application={this.state.application}
            saveApplication={this.saveApplication}
            jumpToStep={this.jumpToStep}
            applicantTypeId={this.state.applicantTypeId}
            setApplicantTypeId={this.setApplicantType}
            managerSubmissions={this.state.managerSubmissions}
            selectedChapterId={this.state.selectedChapterId}
            refreshApplicationState={this.refreshApplicationState}
            copyRatingsToNewTrack={this.copyRatingsToNewTrack}
          />
        ),
      },
      {
        name: 'Profile',
        component: (
          <ApplicantProfileForm
            currentUser={this.props.currentUser}
            saveApplication={this.saveApplication}
            application={this.state.application}
            jumpToStep={this.jumpToStep}
            applicantTypeId={this.state.applicantTypeId}
            educationEntries={this.state.educationEntries}
            careerEntries={this.state.careerEntries}
            handleDeleteCareer={this.handleDeleteCareer}
            handleDeleteEducation={this.handleDeleteEducation}
            createCareer={this.createCareer}
            createEducation={this.createEducation}
            updateCareer={this.updateCareer}
            updateEducation={this.updateEducation}
          />
        ),
      },
      {
        name: 'Relationships',
        component: (
          <RelationshipStep
            application={this.state.application}
            saveApplication={this.saveApplication}
            jumpToStep={this.jumpToStep}
          />
        ),
      },
      {
        name: 'Skills',
        component: (
          <CompetenciesStep
            application={this.state.application}
            applicantTypeId={this.state.applicantTypeId}
            competencies={this.state.competencies}
            ratings={this.state.competenciesRatings}
            getCompetencies={this.getCompetencies}
            createCompetenciesRatings={this.createCompetenciesRatings}
            saveApplication={this.saveApplication}
            jumpToStep={this.jumpToStep}
          />
        ),
      },
      {
        name: 'Essay',
        component: (
          <EssayStep
            application={this.state.application}
            saveApplication={this.saveApplication}
            jumpToStep={this.jumpToStep}
          />
        ),
      },
      {
        name: 'Submit',
        component: (
          <ReviewApplicationStep
            currentUser={this.props.currentUser}
            application={this.state.application}
            saveApplication={this.saveApplication}
            jumpToStep={this.jumpToStep}
            selectedChapterId={this.state.selectedChapterId}
            refreshApplicationState={this.refreshApplicationState}
            clearDraft={this.clearDraftFromLocalStorage}
          />
        ),
      },
    ];

    // Remove Relationships step for Mentees (step index 4)
    if (applicantTypeId === APPLICANT_TYPE_MENTEE) {
      console.log('Removing Relationships step for Mentee');
      steps.splice(4, 1);
    }

    return (
      <ApplicationWindowContextProvider chapter_id={chapter_id}>
        <div style={{ maxWidth: '1140px', margin: '0 auto', padding: '0 15px', marginBottom: '3rem' }}>
          {this.state.step === 0}
          <div>
            <div>
              <h2 style={{ textAlign: 'center', marginTop: '2em' }}>
                CLEAR Program {applicantTypeString} Application
              </h2>
              {this.state.showResumePrompt ? (
                <ResumePrompt
                  lastStep={this.state.draftLastStep}
                  stepNames={steps.map(s => s.name)}
                  onContinue={this.handleResumeContinue}
                  onStartFresh={this.handleStartFresh}
                />
              ) : (
                <div className="step-progress">
                  {showStepsCondition && (
                    <StepIndicator steps={steps} currentStep={this.state.step} />
                  )}
                  <div ref={this.stepContentRef} className={`step-content${this.state.transitioning ? ' step-content--fading' : ''}`}>
                    {steps[this.state.step]?.component}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </ApplicationWindowContextProvider>
    );
  }
}

export default ApplicationContainer;
